// ============================================================
//  BACKGROUND CHECKS PAGE — ADMIN DASHBOARD
//  Path: src/pages/BackgroundChecksPage.tsx
//
//  Experts whose background check needs a human decision land here
//  ('manual_review'). TWO different things arrive in this queue and
//  a rejection means something different for each:
//
//    Certn route  — the platform ran the check and Certn could not
//                   clearly clear it. Rejecting is CONCLUSIVE: the
//                   expert moves to 'failed', which their app renders
//                   as terminal, because re-submitting the same
//                   identity cannot produce a different answer.
//    Upload route — the expert supplied their own report (only
//                   possible when BACKGROUND_CHECK_MODE is 'upload'
//                   or 'both'). Rejecting is FIXABLE: the reason is
//                   written onto their document, they re-upload, and
//                   it comes back here.
//
//  The backend decides which of the two applies from whether an
//  external_background_check document exists — not from which screen
//  the admin happened to use (see reviewByAdmin in
//  backgroundCheck.service.js). The modal below states the
//  consequence before the admin confirms, because "Reject" meaning
//  permanent for one expert and fixable for the next is not something
//  to leave the admin to infer.
//
//  Approving unlocks any hires that were waiting on this expert;
//  rejecting requires a reason.
//
//  Access:
//    super_admin, admin  → full access (approve/reject)
//    customer_service    → view only
// ============================================================

import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { listPendingBackgroundChecks, reviewBackgroundCheck } from '../services/admin.service'
import { Expert } from '../types'
import { useAuth } from '../context/AuthContext'

const BackgroundChecksPage: React.FC = () => {
  const { user } = useAuth()
  const canModify = user?.roles.includes('super_admin') || user?.roles.includes('admin')

  const [experts, setExperts]         = useState<Expert[]>([])
  const [loading, setLoading]         = useState(true)
  const [actionModal, setActionModal] = useState<{ expert: Expert; approved: boolean } | null>(null)
  const [reason, setReason]           = useState('')
  const [submitting, setSubmitting]   = useState(false)
  // FIX: a failed review used to be logged to the console only — the
  // modal just stayed open with no message, so it looked like nothing
  // happened. Shown inside the modal now.
  const [reviewError, setReviewError] = useState('')

  const closeModal = () => {
    setActionModal(null)
    setReason('')
    setReviewError('')
  }

  const fetchPending = async () => {
    setLoading(true)
    try {
      const res = await listPendingBackgroundChecks()
      setExperts(res.data.data)
    } catch (err) {
      console.error('Failed to fetch pending background checks:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchPending() }, [])

  const handleSubmitReview = async () => {
    if (!actionModal || !canModify) return
    if (!actionModal.approved && !reason.trim()) return

    setSubmitting(true)
    setReviewError('')
    try {
      await reviewBackgroundCheck(actionModal.expert.id, {
        approved: actionModal.approved,
        reason:   reason.trim() || undefined,
      })
      closeModal()
      fetchPending()
    } catch (err: any) {
      console.error('Failed to review background check:', err)
      setReviewError(err?.response?.data?.message || (err?.isNetworkError
        ? 'Could not reach the server — check your connection and try again.'
        : 'Could not save this review — try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Background Checks</h1>
          <p className="text-gray-400 text-sm mt-1">{experts.length} awaiting review</p>
        </div>
        {!canModify && (
          <span className="px-3 py-1.5 text-xs rounded-full bg-yellow-600/20 text-yellow-400">View Only</span>
        )}
      </div>

      <div className="grid gap-4">
        {loading ? (
          <div className="text-center py-8 text-gray-400">Loading...</div>
        ) : experts.length === 0 ? (
          <div className="text-center py-8 text-gray-400">Nothing awaiting review right now</div>
        ) : (
          experts.map((expert) => (
            <div key={expert.id} className="bg-surface-card border border-border-light rounded-2xl p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-orange-700 flex items-center justify-center text-white font-bold">
                    {expert.user?.firstName?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-white">{expert.user?.firstName} {expert.user?.lastName}</p>
                    <p className="text-sm text-gray-400">{expert.user?.email || expert.user?.phone || '—'}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Fee paid: {expert.backgroundCheckFeePaidAt ? new Date(expert.backgroundCheckFeePaidAt).toLocaleDateString() : '—'}
                    </p>
                    {/* Which route this expert took. The fee only
                        exists on the Certn route, so its absence is a
                        reliable signal that this is a self-uploaded
                        report to be read before deciding. */}
                    <p className="text-xs mt-1">
                      {expert.backgroundCheckFeePaid ? (
                        <span className="text-gray-400">Platform check (Certn)</span>
                      ) : (
                        <span className="text-blue-400">Uploaded report — open their profile to read it</span>
                      )}
                    </p>
                    {/* FIX: the text above told admin to open the profile
                        but gave no way to get there. */}
                    <Link to={`/experts/${expert.id}`}
                      className="inline-block text-xs text-brand-400 hover:text-brand-300 mt-1">
                      Open profile →
                    </Link>
                  </div>
                </div>
                <span className="px-3 py-1 text-xs rounded-full bg-orange-600/20 text-orange-400">Under Review</span>
              </div>

              {canModify && (
                <div className="mt-4 flex gap-2">
                  <button onClick={() => setActionModal({ expert, approved: true })}
                    className="px-4 py-2 text-sm bg-green-500/10 text-green-400 rounded-xl hover:bg-green-500/20 transition-colors">Approve</button>
                  <button onClick={() => setActionModal({ expert, approved: false })}
                    className="px-4 py-2 text-sm bg-red-500/10 text-red-400 rounded-xl hover:bg-red-500/20 transition-colors">Reject</button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Action Modal */}
      {actionModal && canModify && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-4">
              {actionModal.approved ? 'Approve' : 'Reject'} Background Check
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              {actionModal.expert.user?.firstName} {actionModal.expert.user?.lastName}
            </p>

            {!actionModal.approved && (
              <>
                {/* Say what Reject actually does to THIS expert before
                    it is clicked — see the routes note in the header. */}
                <div className={`rounded-xl p-3 mb-4 text-xs ${
                  actionModal.expert.backgroundCheckFeePaid
                    ? 'bg-red-500/10 border border-red-500/30 text-red-300'
                    : 'bg-blue-500/10 border border-blue-500/30 text-blue-300'
                }`}>
                  {actionModal.expert.backgroundCheckFeePaid
                    ? 'Platform check — rejecting is final. This expert moves to "failed" and cannot fix it by re-submitting. Use this only when the result is genuinely disqualifying.'
                    : 'Uploaded report — rejecting is fixable. Your reason is shown on their document and they can upload a replacement.'}
                </div>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (required — the expert sees this)..." rows={3}
                  className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none mb-4 resize-none" />
              </>
            )}

            {reviewError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4">
                <p className="text-red-400 text-sm">{reviewError}</p>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button onClick={closeModal}
                className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Cancel</button>
              <button onClick={handleSubmitReview} disabled={submitting || (!actionModal.approved && !reason.trim())}
                className={`px-4 py-2 text-sm rounded-xl transition-colors disabled:opacity-50 ${
                  actionModal.approved ? 'bg-green-600 text-white hover:bg-green-500' : 'bg-red-600 text-white hover:bg-red-500'
                }`}>{submitting ? 'Submitting...' : 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BackgroundChecksPage